import React, { Component } from 'react';
import { Flex, Box } from 'core/components';
import styled from 'styled-components';

const ResizableContainer = styled(Flex)`
  height: 100%;
  width: 100%;
  overflow: visible;
`;

const ResizablePanel = styled(Flex)`
  height: 100%;
  width: 100%;
  overflow: visible;
`;

const ResizeHandle = styled(Box)`
  width: 1px;
  background: ${({ theme }) => theme.colors.gray5};
  cursor: col-resize;
  position: relative;
  flex-shrink: 0;
  transition: all 0.15s ease;
  z-index: 9999;

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
  }

  &:active {
    background: ${({ theme }) => theme.colors.primary};
  }

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 8px;
    transition: all 0.15s ease;
    height: 40px;
    background: ${({ theme }) => theme.colors.gray5};
    border: 1px solid ${({ theme }) => theme.colors.gray4};
    border-radius: 8px;
  }

  &:hover::before {
    background: ${({ theme }) => theme.colors.primary};
    border-color: ${({ theme }) => theme.colors.primary};
    opacity: 0.75;
    height: 60px;
  }

  &:active::before {
    opacity: 1;
  }
`;

/**
 * ResizablePanels - A component that provides two resizable panels with a draggable separator
 *
 * @param {React.ReactNode} leftPanel - Content for the left panel
 * @param {React.ReactNode} rightPanel - Content for the right panel
 * @param {boolean} showRightPanel - Whether to show the right panel (default: true)
 * @param {number} defaultLeftWidth - Default width percentage for left panel (0-100)
 * @param {number} leftMinWidth - Minimum width in pixels for the left panel (default: 0)
 * @param {number} rightMinWidth - Minimum width in pixels for the right panel (default: 0)
 * @param {function} onResize - Callback fired when panels are resized (percentage)
 */
class ResizablePanels extends Component {
  state = {
    isDragging: false
  };

  containerRef = React.createRef();

  componentDidMount() {
    const { defaultLeftWidth } = this.props;
    if (defaultLeftWidth) {
      this.setState({ leftWidth: defaultLeftWidth });
    }

    window.addEventListener('resize', this.handleWindowResize);
  }

  componentWillUnmount() {
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
    window.removeEventListener('resize', this.handleWindowResize);
  }

  handleWindowResize = () => {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    this.resizeTimeout = setTimeout(() => {
      this.forceUpdate();
    }, 100);
  };

  handleMouseDown = (e) => {
    e.preventDefault();
    this.setState({ isDragging: true });
    document.addEventListener('mousemove', this.handleMouseMove);
    document.addEventListener('mouseup', this.handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  handleMouseMove = (e) => {
    const { onResize, leftMinWidth = 0, rightMinWidth = 0 } = this.props;
    const { isDragging } = this.state;

    if (!isDragging || !this.containerRef.current) {
      return;
    }

    const container = this.containerRef.current;
    const containerRect = container.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const mouseX = e.clientX - containerRect.left;

    let newLeftWidth = (mouseX / containerWidth) * 100;

    // Apply minWidth constraints in pixels, convert to percentages
    const leftMinPercent = (leftMinWidth / containerWidth) * 100;
    const rightMinPercent = (rightMinWidth / containerWidth) * 100;
    const maxLeftPercent = 100 - rightMinPercent;

    // Apply constraints: respect minWidths and fallback to 20%/80% if no minWidths specified
    const minLeft = leftMinPercent > 0 ? leftMinPercent : 20;
    const maxLeft = rightMinPercent > 0 ? maxLeftPercent : 80;

    newLeftWidth = Math.max(minLeft, Math.min(maxLeft, newLeftWidth));

    this.setState({ leftWidth: newLeftWidth });

    if (onResize) {
      onResize(newLeftWidth);
    }
  };

  handleMouseUp = () => {
    this.setState({ isDragging: false });
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  };

  render() {
    const { leftPanel, rightPanel, showRightPanel = true, ...flexProps } = this.props;
    const { leftWidth, isDragging } = this.state;

    if (!showRightPanel) {
      return (
        <ResizableContainer {...flexProps}>
          <ResizablePanel flex={1}>{leftPanel}</ResizablePanel>
        </ResizableContainer>
      );
    }

    const rightWidth = 100 - leftWidth;

    return (
      <ResizableContainer
        {...flexProps}
        ref={this.containerRef}
        style={{ cursor: isDragging ? 'col-resize' : 'default' }}
      >
        <ResizablePanel style={{ width: `${leftWidth}%` }}>{leftPanel}</ResizablePanel>
        <ResizeHandle onMouseDown={this.handleMouseDown} role="separator" aria-label="Resize panels" tabIndex={0} />
        <ResizablePanel style={{ width: `${rightWidth}%` }}>{rightPanel}</ResizablePanel>
      </ResizableContainer>
    );
  }
}

export default ResizablePanels;
